import { useEffect } from 'react';

declare global {
    interface Window {

        dataLayer: any[];

        initCookieConsent: (...args: any[]) => any;
        gtag: (...args: unknown[]) => void;
        fbq: (...args: unknown[]) => void;
        clarity: (...args: unknown[]) => void;
        analyticsScriptSetUp?: boolean;
    }
}

export interface TrackingEvent {
    eventName: string;
    eventLabel: string;
    eventCategory: string;
    nonInteraction?: boolean;
    value?: number;
    currency?: string;
}

export function enableGoogleAnalytics(streamId: string) {
    console.debug('Enabling Google Analytics');
    window.dataLayer = window.dataLayer || [];

    window.gtag = function() {
         
        window.dataLayer.push(arguments);
    };

    window.gtag('js', new Date());

    window.gtag('consent', 'default', {
        'ad_storage': 'denied',
        'analytics_storage': 'denied',
        'wait_for_update': 500
    });

    window.gtag('config', streamId);
}

export function enableMsClarity(projectId: string) {
    console.debug('Enabling MS Clarity');

    const script = document.createElement('script');

    script.innerHTML = `(function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", "${projectId}");
    clarity("consent", false);`;

    document.body.appendChild(script);
}

export function enableFacebookPixel(pixelId: string) {
    console.debug('Enabling Facebook Pixel');

    const script = document.createElement('script');

    script.innerHTML = `
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('consent', 'revoke');
        fbq('init', '${pixelId}');
        fbq('track', 'PageView');
    `;

    document.body.appendChild(script);
}

export function setupCookieConsent(locale?: string) {
    if (!('initCookieConsent' in window)) {
        console.warn('Cookie consent script not loaded.');
        return;
    }

    const cookieconsent = window.initCookieConsent();

    const currentLang = [
        'cs',
        'en'
    ].includes(locale ?? '')
        ? locale
        : 'en';

    cookieconsent.run({
        current_lang: currentLang,
        autoclear_cookies: true,
        page_scripts: true,
        gui_options: {
            consent_modal: {
                layout: 'cloud',
                position: 'bottom center',
                transition: 'slide',
                swap_buttons: false
            },
            settings_modal: {
                layout: 'box',
                transition: 'slide'
            }
        },
        onAccept: () => {
            if (cookieconsent.allowedCategory('analytics')) {
                console.debug('Cookie consent granted for analytics.');

                if (window.gtag !== undefined) {
                    window.gtag('consent', 'update', {
                        'analytics_storage': 'granted'
                    });
                }

                if (window.fbq !== undefined) {
                    window.fbq('consent', 'grant');
                }

                if (window.clarity !== undefined) {
                    window.clarity('consent', true);
                }

                trackAnalyticsEvent({
                    eventName: 'page_load_consent',
                    eventLabel: 'Page loaded with Cookies enabled',
                    eventCategory: 'general'
                });
            }
        },
        onChange: () => {
            if (cookieconsent.allowedCategory('analytics')) {
                console.debug('Cookie consent granted for analytics.');

                if (window.gtag !== undefined) {
                    window.gtag('consent', 'update', {
                        'analytics_storage': 'granted'
                    });
                }

                if (window.fbq !== undefined) {
                    window.fbq('consent', 'grant');
                }

                if (window.clarity !== undefined) {
                    window.clarity('consent', true);
                }
            } else {
                console.debug('Cookie consent denied for analytics.');

                if (window.gtag !== undefined) {
                    window.gtag('consent', 'update', {
                        'analytics_storage': 'denied'
                    });
                }

                if (window.fbq !== undefined) {
                    window.fbq('consent', 'revoke');
                }

                if (window.clarity !== undefined) {
                    window.clarity('consent', false);
                }
            }
        },
        languages: {
            'en': {
                consent_modal: {
                    title: 'We use cookies',
                    description: 'Hi, this website uses essential cookies to ensure its proper operation and tracking cookies to understand how you interact with it.',
                    primary_btn: {
                        text: 'Accept all',
                        role: 'accept_all'              // 'accept_selected' or 'accept_all'
                    },
                    secondary_btn: {
                        text: 'Settings',
                        role: 'settings'                // 'settings' or 'accept_necessary'
                    }
                },
                settings_modal: {
                    title: 'Cookie preferences',
                    save_settings_btn: 'Save settings',
                    accept_all_btn: 'Accept all',
                    reject_all_btn: 'Reject all',
                    close_btn_label: 'Close',
                    cookie_table_headers: [
                        {col1: 'Name'},
                        {col2: 'Domain'},
                        {col3: 'Expiration'},
                        {col4: 'Description'},
                    ],
                    blocks: [
                        {
                            title: 'Cookie usage',
                            description: 'We use cookies to ensure the basic functionalities of the website and to enhance your online experience. You can choose for each category to opt-in/out whenever you want.'
                        }, {
                            title: 'Strictly necessary cookies',
                            description: 'These cookies are essential for the proper functioning of this website. Without these cookies, the website would not work properly',
                            toggle: {
                                value: 'necessary',
                                enabled: true,
                                readonly: true
                            },
                            cookie_table: [
                                {
                                    col1: 'refreshToken',
                                    col2: 'app.aikorektury.cz',
                                    col3: '-',
                                    col4: 'First party cookie used to store your session so we can keep you logged in.',
                                    is_regex: true
                                },
                            ]
                        }, {
                            title: 'Performance and Analytics cookies',
                            description: 'These cookies collect information about how you use the website, which pages you visited and which links you clicked on. All of the data is anonymized and cannot be used to identify you.',
                            toggle: {
                                value: 'analytics',
                                enabled: true,
                                readonly: false
                            },
                            cookie_table: [
                                {
                                    col1: '^_ga',
                                    col2: 'google.com',
                                    col3: '2 years',
                                    col4: 'Those cookies are installed by Google Analytics, calculates visitor, session and campaign data and also keeps track of site usage for the site\'s analytics report. The cookie stores information anonymously and assigns a randomly generated number to recognize unique visitors.',
                                    is_regex: true
                                },
                                {
                                    col1: '_gid',
                                    col2: 'google.com',
                                    col3: '1 day',
                                    col4: 'Installed by Google Analytics, _gid cookie stores information on how visitors use a website, while also creating an analytics report of the website\'s performance. Some of the data that are collected include the number of visitors, their source, and the pages they visit anonymously.',
                                },
                                {
                                    col1: '_fbp',
                                    col2: 'facebook.com',
                                    col3: '2 years',
                                    col4: 'This cookie is set by Facebook to display advertisements when either on Facebook or on a digital platform powered by Facebook advertising, after visiting the website.',
                                },
                                {
                                    col1: '_fr',
                                    col2: 'facebook.com',
                                    col3: '2 years',
                                    col4: 'Facebook sets this cookie to show relevant advertisements to users by tracking user behaviour across the web, on sites that have Facebook pixel or Facebook social plugin.',
                                },
                                {
                                    col1: '^PREF',
                                    col2: 'youtube.com',
                                    col3: '8 months',
                                    col4: 'We embed videos from our official YouTube channel using YouTube\'s privacy-enhanced mode. This mode may set cookies on your computer once you click on the YouTube video player, but YouTube will not store personally-identifiable cookie information for playbacks of embedded videos using the privacy-enhanced mode. Read more at YouTube\'s embedding videos information page.',
                                    is_regex: true
                                },
                                {
                                    col1: '^VISITOR_INFO1_LIVE',
                                    col2: 'youtube.com',
                                    col3: '8 months',
                                    col4: 'We embed videos from our official YouTube channel using YouTube\'s privacy-enhanced mode. This mode may set cookies on your computer once you click on the YouTube video player, but YouTube will not store personally-identifiable cookie information for playbacks of embedded videos using the privacy-enhanced mode. Read more at YouTube\'s embedding videos information page.',
                                    is_regex: true
                                },
                                {
                                    col1: '^VSC',
                                    col2: 'youtube.com',
                                    col3: 'at the end of your session',
                                    col4: 'We embed videos from our official YouTube channel using YouTube\'s privacy-enhanced mode. This mode may set cookies on your computer once you click on the YouTube video player, but YouTube will not store personally-identifiable cookie information for playbacks of embedded videos using the privacy-enhanced mode. Read more at YouTube\'s embedding videos information page.',
                                },
                                {
                                    col1: '^remote_sid',
                                    col2: 'youtube.com',
                                    col3: 'at the end of your session',
                                    col4: 'We embed videos from our official YouTube channel using YouTube\'s privacy-enhanced mode. This mode may set cookies on your computer once you click on the YouTube video player, but YouTube will not store personally-identifiable cookie information for playbacks of embedded videos using the privacy-enhanced mode. Read more at YouTube\'s embedding videos information page.',
                                }
                            ]
                        }, {
                            title: 'More information',
                            description: 'For any queries in relation to our policy on cookies and your choices, please <a class="cc-link" href="#yourcontactpage">contact us</a>.',
                        }
                    ]
                }
            },
            'cs': {
                consent_modal: {
                    title: 'Používáme cookies',
                    description: 'Dobrý den, tato webová stránka používá nezbytné cookies pro zajištění jejího správného fungování a sledovací cookies pro pochopení toho, jak s ní interagujete.',
                    primary_btn: {
                        text: 'Přijmout vše',
                        role: 'accept_all'
                    },
                    secondary_btn: {
                        text: 'Nastavení',
                        role: 'settings'
                    }
                },
                settings_modal: {
                    title: 'Předvolby cookies',
                    save_settings_btn: 'Uložit nastavení',
                    accept_all_btn: 'Přijmout vše',
                    reject_all_btn: 'Odmítnout vše',
                    close_btn_label: 'Zavřít',
                    cookie_table_headers: [
                        {col1: 'Název'},
                        {col2: 'Doména'},
                        {col3: 'Expirace'},
                        {col4: 'Popis'},
                    ],
                    blocks: [
                        {
                            title: 'Použití cookies',
                            description: 'Používáme cookies k zajištění základních funkcí webu a ke zlepšení vašeho online zážitku. Můžete si vybrat, zda chcete souhlasit/nebo ne pro každou kategorii.'
                        }, {
                            title: 'Nepostradatelné cookies',
                            description: 'Tyto cookies jsou nezbytné pro správné fungování tohoto webu.',
                            toggle: {
                                value: 'necessary',
                                enabled: true,
                                readonly: true
                            },
                            cookie_table: [
                                {
                                    col1: 'refreshToken',
                                    col2: 'app.aikorektury.cz',
                                    col3: '-',
                                    col4: 'Cookie první strany používaná k uložení vašeho přihlášení.',
                                    is_regex: true
                                }
                            ]
                        }, {
                            title: 'Cookies pro výkonnost a analytiku',
                            description: 'Tyto cookies sbírají informace o tom, jak používáte web, které stránky jste navštívili a na které odkazy jste klikli. Všechna data jsou anonymní a nemohou být použita k vaší identifikaci.',
                            toggle: {
                                value: 'analytics',
                                enabled: true,
                                readonly: false
                            },
                            cookie_table: [
                                {
                                    col1: '^_ga',
                                    col2: 'google.com',
                                    col3: '2 roky',
                                    col4: 'Tyto cookies instaluje Google Analytics, vypočítává data o návštěvnících, sezeních a kampaních a také sleduje využití webu pro analýzu webu. Cookie ukládá informace anonymně a přiřazuje náhodně vygenerované číslo k rozpoznání jedinečných návštěvníků.',
                                    is_regex: true
                                },
                                {
                                    col1: '_gid',
                                    col2: 'google.com',
                                    col3: '1 den',
                                    col4: 'Instalováno Google Analytics, _gid cookie ukládá informace o tom, jak návštěvníci používají webovou stránku, zároveň vytváří analytickou zprávu o výkonu webu. Některá z shromažďovaných dat zahrnují počet návštěvníků, jejich zdroj a stránky, které navštívili anonymně.',
                                },
                                {
                                    col1: '_fbp',
                                    col2: 'facebook.com',
                                    col3: '2 roky',
                                    col4: 'Tuto cookie nastavuje Facebook pro zobrazení reklam, ať už na Facebooku nebo na digitální platformě poháněné reklamou od Facebooku, po návštěvě webové stránky.',
                                },
                                {
                                    col1: '_fr',
                                    col2: 'facebook.com',
                                    col3: '2 roky',
                                    col4: 'Facebook nastavuje tuto cookie pro zobrazení relevantních reklam uživatelům sledováním chování uživatele na webu, na stránkách, které mají Facebook pixel nebo Facebook sociální plugin.',
                                },
                                {
                                    col1: '^PREF',
                                    col2: 'youtube.com',
                                    col3: '8 měsíců',
                                    col4: 'Vkládáme videa z našeho oficiálního kanálu YouTube pomocí zvýšeného soukromí YouTube. Tento režim může na vašem počítači nastavit cookies, jakmile kliknete na přehrávač videa YouTube, ale YouTube nebude ukládat informace umožňující osobní identifikaci pro přehrávání vložených videí v režimu s vyšším soukromím. Více informací najdete na informační stránce YouTube o vkládání videí.',
                                    is_regex: true
                                },
                                {
                                    col1: '^VISITOR_INFO1_LIVE',
                                    col2: 'youtube.com',
                                    col3: '8 měsíců',
                                    col4: 'Vkládáme videa z našeho oficiálního kanálu YouTube pomocí zvýšeného soukromí YouTube. Tento režim může na vašem počítači nastavit cookies, jakmile kliknete na přehrávač videa YouTube, ale YouTube nebude ukládat informace umožňující osobní identifikaci pro přehrávání vložených videí v režimu s vyšším soukromím. Více informací najdete na informační stránce YouTube o vkládání videí.',
                                    is_regex: true
                                },
                                {
                                    col1: '^VSC',
                                    col2: 'youtube.com',
                                    col3: 'na konci vašeho sezení',
                                    col4: 'Vkládáme videa z našeho oficiálního kanálu YouTube pomocí zvýšeného soukromí YouTube. Tento režim může na vašem počítači nastavit cookies, jakmile kliknete na přehrávač videa YouTube, ale YouTube nebude ukládat informace umožňující osobní identifikaci pro přehrávání vložených videí v režimu s vyšším soukromím. Více informací najdete na informační stránce YouTube o vkládání videí.',
                                },
                                {
                                    col1: '^remote_sid',
                                    col2: 'youtube.com',
                                    col3: 'na konci vašeho sezení',
                                    col4: 'Vkládáme videa z našeho oficiálního kanálu YouTube pomocí zvýšeného soukromí YouTube. Tento režim může na vašem počítači nastavit cookies, jakmile kliknete na přehrávač videa YouTube, ale YouTube nebude ukládat informace umožňující osobní identifikaci pro přehrávání vložených videí v režimu s vyšším soukromím. Více informací najdete na informační stránce YouTube o vkládání videí.',
                                }
                            ]
                        }
                    ]
                }
            }
        }
    });
}

export const useAnalytics = () => {
    const googleAnalyticsStreamId = import.meta.env.VITE_GA_STREAM_ID as string;
    const facebookPixelId = import.meta.env.VITE_FB_PIXEL_ID as string;
    const clarityProjectId = import.meta.env.VITE_CLARITY_PROJECT_ID as string;

    const locale = navigator.language;

    useEffect(() => {
        console.debug('Running analytics scripts setup...', {
            googleAnalyticsStreamId,
            facebookPixelId,
            clarityProjectId
        });

        // Add script to head
        if (googleAnalyticsStreamId) {
            const gaScript = document.createElement('script');
            gaScript.async = true;
            gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsStreamId}`;
            document.head.appendChild(gaScript);
        }

        if (window.analyticsScriptSetUp) {
            return;
        }

        window.analyticsScriptSetUp = true;

        if (googleAnalyticsStreamId) {
            enableGoogleAnalytics(googleAnalyticsStreamId);
        }

        if (facebookPixelId) {
            enableFacebookPixel(facebookPixelId);
        }

        if (clarityProjectId) {
            enableMsClarity(clarityProjectId);
        }

        setupCookieConsent(locale);
    }, []);
};

export const trackAnalyticsEvent = (event: TrackingEvent) => {
    console.debug('Track event', event);

    try {
        if (window.gtag !== undefined) {
            window.gtag('event', event.eventName, {
                event_label: event.eventLabel,
                event_category: event.eventCategory,
                non_interaction: false
            });
        }

        if (window.fbq !== undefined && !event.nonInteraction) {
            window.fbq('trackCustom', 'LinkClick_' + event.eventCategory, {
                event_name: event.eventLabel,
                event_category: event.eventCategory,
                non_interaction: false,
                value: event.value,
                currency: event.currency
            });
        }
    } catch (err) {
        console.error('Failed to track analytics event', err);
    }
};


export const trackBucketAnalyticsEvent = (event: TrackingEvent, value: number, buckets: number[]) => {
    const sortedBuckets = buckets.sort((a, b) => a - b);

    let bucketName = sortedBuckets[0] + 'orLess';
    let bucketLabel = '<' + sortedBuckets[0];

    for (let i = 0; i < sortedBuckets.length; i++) {
        if (value >= sortedBuckets[i]) {
            // Define bucket name as `bucket_<from>_<to>`
            bucketName = `${sortedBuckets[i]}` + (i < sortedBuckets.length - 1 ? `_${sortedBuckets[i + 1] - 1}` : 'orMore');
            bucketLabel = `${sortedBuckets[i]}` + (i < sortedBuckets.length - 1 ? `-${sortedBuckets[i + 1] - 1}` : '+');
        }
    }

    trackAnalyticsEvent({
        ...event,
        eventName: `${event.eventName}__bucket_${bucketName}`,
        eventLabel: `${event.eventLabel} (Bucket ${bucketLabel})`
    });
};
